/**
 * PaymentHistory — Patient canonical payment visibility
 * ======================================================
 *
 * FE-WIRING-2: reads exclusively from the canonical commercial chain
 * `quotes → financial_contracts → payments` via
 *   GET /api/payments/patient/{patient_id}
 * proxied through `PaymentContext.fetchPatientPayments`.
 *
 * R-7 (FE_FORBIDDEN_SURFACES) — patient must NOT see:
 *   • commission internals (`commission_amount`, `commission_rate_bps`,
 *     `commission_mode`)
 *   • clinic receivable / payout internals
 *     (`clinic_receivable_amount`, `psp_*`, reconciliation fields)
 *   • refund / invoice / webhook internals
 * This page renders ONLY the public payment surface
 * (amount, currency, status, payment_type, created_at, paid_at,
 * provider name + country derived from `clinic_info`).
 */
import React, { useEffect, useState, useCallback } from 'react';
import { formatDateLocale as _fmtDate } from '../../utils/dateFormat';
import { usePayment } from '../../context/PaymentContext';
import { Card, CardContent } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Loader2, CreditCard, ArrowDownLeft, Clock, CheckCircle, XCircle, AlertTriangle } from 'lucide-react';

const STATUS_CONFIG = {
  created: { label: 'Created', variant: 'outline', icon: Clock },
  pending_customer_action: { label: 'Action Required', variant: 'warning', icon: AlertTriangle },
  processing: { label: 'Processing', variant: 'secondary', icon: Clock },
  authorized: { label: 'Authorized', variant: 'secondary', icon: CheckCircle },
  paid: { label: 'Paid', variant: 'default', icon: CheckCircle },
  partially_refunded: { label: 'Partial Refund', variant: 'warning', icon: ArrowDownLeft },
  refunded: { label: 'Refunded', variant: 'destructive', icon: ArrowDownLeft },
  failed: { label: 'Failed', variant: 'destructive', icon: XCircle },
  cancelled: { label: 'Cancelled', variant: 'outline', icon: XCircle },
  expired: { label: 'Expired', variant: 'outline', icon: Clock },
  settled: { label: 'Settled', variant: 'default', icon: CheckCircle },
  disputed: { label: 'Disputed', variant: 'destructive', icon: AlertTriangle },
};

function StatusBadge({ status }) {
  const config = STATUS_CONFIG[status] || { label: status, variant: 'outline', icon: Clock };
  const Icon = config.icon;
  return (
    <Badge data-testid={`payment-status-${status}`} variant={config.variant} className="gap-1 capitalize">
      <Icon className="h-3 w-3" />
      {config.label}
    </Badge>
  );
}

function formatCurrency(amount, currency = 'EUR') {
  return new Intl.NumberFormat('en-US', { style: 'currency', currency }).format(amount);
}

function formatDateLocale(dateStr) {
  if (!dateStr) return '-';
  return _fmtDate(dateStr);
}

export default function PaymentHistory() {
  const { fetchPatientPayments, loading, error } = usePayment();
  const [payments, setPayments] = useState([]);
  const [loaded, setLoaded] = useState(false);

  const loadPayments = useCallback(async () => {
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    const patientId = user.id || user.patient_id || '';
    if (!patientId) return;
    const result = await fetchPatientPayments(patientId);
    setPayments(result || []);
    setLoaded(true);
  }, [fetchPatientPayments]);

  useEffect(() => {
    loadPayments();
  }, [loadPayments]);

  if (loading && !loaded) {
    return (
      <div data-testid="payment-history-loading" className="flex items-center justify-center py-16">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div data-testid="payment-history-page" className="space-y-6 p-4 max-w-4xl mx-auto">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold tracking-tight">Payment History</h1>
        <Badge variant="outline" className="text-xs">
          {payments.length} payment{payments.length !== 1 ? 's' : ''}
        </Badge>
      </div>

      {error && (
        <Card className="border-destructive">
          <CardContent className="pt-4">
            <p data-testid="payment-history-error" className="text-sm text-destructive">{error}</p>
          </CardContent>
        </Card>
      )}

      {loaded && payments.length === 0 && !error && (
        <Card>
          <CardContent className="pt-6 text-center">
            <CreditCard className="h-12 w-12 mx-auto text-muted-foreground mb-3" />
            <p data-testid="payment-history-empty" className="text-muted-foreground">No payment records yet</p>
            <p className="text-xs text-muted-foreground mt-1">
              Payments will appear here once you proceed with an offer
            </p>
          </CardContent>
        </Card>
      )}

      <div className="space-y-3">
        {payments.map((payment) => (
          <Card key={payment.payment_id} data-testid={`payment-card-${payment.payment_id}`} className="hover:shadow-sm transition-shadow">
            <CardContent className="pt-4">
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="font-semibold text-lg">
                      {formatCurrency(payment.amount, payment.currency)}
                    </span>
                    <StatusBadge status={payment.status} />
                  </div>
                  <p className="text-sm text-muted-foreground truncate">
                    {payment.clinic_info?.clinic_name || payment.clinic_id}
                    {payment.clinic_info?.country ? ` - ${payment.clinic_info.country}` : ''}
                  </p>
                  <div className="flex items-center gap-4 mt-2 text-xs text-muted-foreground">
                    <span className="capitalize">{payment.payment_type}</span>
                    <span>{formatDateLocale(payment.created_at)}</span>
                  </div>
                </div>
                <div className="text-right text-xs space-y-1">
                  {/* FE-WIRING-2 / R-7: commission, clinic_receivable, PSP and
                      other finance internals MUST NOT be rendered for the
                      patient. We only echo back the public `paid_at` event
                      timestamp. */}
                  {payment.paid_at && (
                    <div className="text-green-600" data-testid={`payment-paid-at-${payment.payment_id}`}>
                      Paid {formatDateLocale(payment.paid_at)}
                    </div>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
}
